import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { countries, getCountryByCode, searchCountries } from '../../constants/countriesData';
import { formatPhoneNumber, getPhonePlaceholder } from '../../utils/function';
import './PhoneInputWithCountry.css';

// Constants
const DEFAULT_PLACEHOLDER = '(555) 123-4567';
const DEFAULT_COUNTRY = 'US';
const PHONE_FORMAT = 'bracket';

/**
 * PhoneInputWithCountry - A reusable phone input component with country selection
 * @param {Object} props - Component props
 * @param {string} props.value - Current phone number value
 * @param {Function} props.onChange - Callback function called when phone number or country changes
 * @param {string} props.placeholder - Placeholder text for phone input
 * @param {string} props.defaultCountry - Default country code (e.g., 'US')
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props...props - Additional props passed to the phone input
 */
const PhoneInputWithCountry = ({
  value = '',
  onChange,
  placeholder = DEFAULT_PLACEHOLDER,
  defaultCountry = DEFAULT_COUNTRY,
  className = '',
  ...props
}) => {
  // State management
  const [selectedCountry, setSelectedCountry] = useState(() => 
    getCountryByCode(defaultCountry) || countries[0]
  );
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [phoneNumber, setPhoneNumber] = useState(value);
  
  // Refs
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Memoized filtered countries to prevent unnecessary recalculations
  const filteredCountries = useMemo(() => 
    searchCountries(searchQuery), 
    [searchQuery]
  );

  // Memoized phone data object to prevent unnecessary re-renders
  const phoneData = useMemo(() => ({
    countryCode: selectedCountry.code,
    countryName: selectedCountry.name,
    dialCode: selectedCountry.dialCode,
    phoneNumber: phoneNumber,
    fullNumber: `${selectedCountry.dialCode}${phoneNumber}`
  }), [selectedCountry, phoneNumber]);

  // Memoized placeholder based on selected country
  const dynamicPlaceholder = useMemo(() => 
    getPhonePlaceholder(selectedCountry.code),
    [selectedCountry.code]
  );

  // Callback to trigger onChange with current phone data
  const triggerOnChange = useCallback(() => {
    if (onChange && typeof onChange === 'function') {
      onChange(phoneData);
    }
  }, [onChange, phoneData]);

  // Handle country selection
  const handleCountrySelect = useCallback((country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery('');
    
    // Clear phone number when country changes
    setPhoneNumber('');
    
    // Update phone number with new country
    const newPhoneData = {
      ...phoneData,
      countryCode: country.code,
      countryName: country.name,
      dialCode: country.dialCode,
      phoneNumber: '',
      fullNumber: `${country.dialCode}`
    };
    
    if (onChange && typeof onChange === 'function') {
      onChange(newPhoneData);
    }
  }, [phoneNumber, onChange, phoneData]);

  // Handle phone number input
  const handlePhoneChange = useCallback((e) => {
    const inputValue = e.target.value;
    const formattedValue = formatPhoneNumber(inputValue, PHONE_FORMAT, selectedCountry.code);
    setPhoneNumber(formattedValue);
    
    // Trigger onChange with updated phone number
    const newPhoneData = {
      ...phoneData,
      phoneNumber: formattedValue,
      fullNumber: `${selectedCountry.dialCode}${formattedValue}`
    };
    
    if (onChange && typeof onChange === 'function') {
      onChange(newPhoneData);
    }
  }, [selectedCountry, onChange, phoneData]);

  // Update phone number when value prop changes
  useEffect(() => {
    if (value !== phoneNumber) {
      setPhoneNumber(value);
    }
  }, [value, phoneNumber]);

  // Handle dropdown toggle
  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => {
      const newState = !prev;
      if (!newState) {
        setSearchQuery('');
      }
      return newState;
    });
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setSearchQuery('');
    }
  }, []);

  // Handle search input change
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  // Handle search input key down
  const handleSearchKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setIsDropdownOpen(false);
      setSearchQuery('');
    }
  }, []);

  return (
    <div className={`phone-input-container ${className} ${isDropdownOpen ? 'drop_open' : ''}`}>
      <div className="phone-input-wrapper">
        {/* Country Selector */}
        <div className="country-selector" ref={dropdownRef}>
          <button
            type="button"
            className="country-selector-button"
            onClick={toggleDropdown}
            onKeyDown={handleKeyDown}
            aria-expanded={isDropdownOpen}
            aria-haspopup="listbox"
            aria-label={`Selected country: ${selectedCountry.name} (${selectedCountry.dialCode})`}
          >
            <span className="country-flag" aria-hidden="true">
              {selectedCountry.flag}
            </span>
            <span className="country-dial-code">
              {selectedCountry.dialCode}
            </span>
            <i 
              className={`fas fa-chevron-down dropdown-icon ${isDropdownOpen ? 'open' : ''}`}
              aria-hidden="true"
            />
          </button>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div 
              className="country-dropdown"
              role="listbox"
              aria-label="Country selection"
            >
              <div className="country-search">
                <input
                  type="text"
                  placeholder="Search countries..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  className="country-search-input"
                  autoComplete="off"
                  autoFocus
                  aria-label="Search countries"
                />
                <i className="fas fa-search search-icon" aria-hidden="true" />
              </div>

              <div className="country-list" role="listbox">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country) => (
                    <button
                      key={country.code}
                      type="button"
                      className={`country-option ${selectedCountry.code === country.code ? 'selected' : ''}`}
                      onClick={() => handleCountrySelect(country)}
                      role="option"
                      aria-selected={selectedCountry.code === country.code}
                      aria-label={`${country.name} (${country.dialCode})`}
                    >
                      <span className="country-flag" aria-hidden="true">
                        {country.flag}
                      </span>
                      <span className="country-name">
                        {country.name}
                      </span>
                      <span className="country-dial-code">
                        {country.dialCode}
                      </span>
                    </button>
                  ))
                ) : (
                  <div className="no-results" role="status" aria-live="polite">
                    <i className="fas fa-search" aria-hidden="true" />
                    <span>No countries found</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <div className="phone-input-field">
          <input
            ref={inputRef}
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder={dynamicPlaceholder}
            className="phone-number-input"
            autoComplete="off"
            aria-label="Phone number"
            aria-describedby="phone-format-hint"
            {...props}
          />
        </div>
      </div>
      
      {/* Hidden hint for screen readers */}
      <div id="phone-format-hint" className="sr-only">
        Enter phone number for {selectedCountry.name} (format: {dynamicPlaceholder})
      </div>
    </div>
  );
};

export default PhoneInputWithCountry;
